import { comparer, computed, makeObservable, observable, onBecomeObserved, onBecomeUnobserved, reaction, untracked, type IEqualsComparer } from 'mobx';

import { toError } from 'helpers/toError';

/**
 * How long a query stays armed after its last observer leaves.
 *
 * A de-observation is not the same as "nobody wants this any more": React
 * re-renders, StrictMode's mount → unmount → mount, and a Suspense retry all
 * drop and re-take observation within a few ticks. Tearing down immediately
 * would turn each of those into a fresh round trip, so the reaction outlives a
 * brief gap and only dies if the gap persists.
 */
export const DEFAULT_GRACE_MS = 1_000;

/** A settled answer, and the instant the request behind it went out. */
export interface QueryAnswer<T> {
  readonly value: T;
  readonly asOf: Date;
}

/**
 * What a query can currently tell its consumer.
 *
 * `stale` exists so a moved parameter does not blank the screen: the previous
 * answer keeps rendering while the next one is on its way, which is what makes a
 * live-updating panel readable rather than flickering. `error` carries `previous`
 * for the same reason — a failed *refresh* must not erase an answer that was
 * fine a moment ago.
 */
export type QueryValue<T> =
  /** Nothing asked yet, and nothing on the way. A query only asks once observed. */
  | { readonly kind: 'uninitialized' }
  /** First answer in flight; there is nothing to show yet. */
  | { readonly kind: 'loading' }
  | { readonly kind: 'resolved'; readonly answer: QueryAnswer<T> }
  /** A newer answer is on its way; render `previous` until it lands. */
  | { readonly kind: 'stale'; readonly previous: QueryAnswer<T> }
  | { readonly kind: 'error'; readonly error: Error; readonly previous: QueryAnswer<T> | null };

/**
 * The answer a value holds, whichever member holds it.
 *
 * `resolved`, `stale` and `error` can all carry one, and the common consumer shape
 * is "render what we have, dimmed if it is stale" — so all three carry the same
 * {@link QueryAnswer} rather than each spelling it differently, and this exists so
 * no consumer has to rewrite the three-branch narrowing.
 */
export const answerOf = <T>(value: QueryValue<T>): QueryAnswer<T> | null => {
  switch (value.kind) {
    case 'resolved':
      return value.answer;
    case 'stale':
      return value.previous;
    case 'error':
      return value.previous;
    case 'uninitialized':
    case 'loading':
      return null;
  }
};

export interface MobxQueryOptions<P, T> {
  /**
   * Everything the answer depends on, read reactively. Keep it pure: an impure
   * input (the clock, `Math.random`) cannot be compared, so a change in it can
   * never trigger a recomputation — read those inside `fetch` instead and drive
   * recomputation with `refresh`.
   */
  readonly params: () => P;
  /** Asks for the answer. Rejections become `{ kind: 'error' }`. */
  readonly fetch: (params: P) => Promise<T>;
  /**
   * Collapses a burst of parameter changes into one call. The value goes `stale`
   * immediately either way — the delay defers the request, not the admission that
   * the answer is out of date. `0` means no delay.
   *
   * Required, with no default, because whether the parameters are keystroke-shaped
   * or event-shaped is something only the consumer knows, and both defaults fail
   * silently: `0` gives a request per keystroke, while a non-zero one taxes a
   * discrete selection with dead air. Note it does NOT apply to the first request,
   * which goes out as soon as the value is observed.
   */
  readonly debounceMs: number;
  /** Defaults to structural equality, which is what a fresh params object needs. */
  readonly equals?: IEqualsComparer<P>;
  /** See {@link DEFAULT_GRACE_MS}. `0` tears down as soon as the last observer leaves. */
  readonly graceMs?: number;
}

/**
 * The settled half of the state: the last thing the server said, if anything.
 * Orthogonal to `Phase` — a query can be re-fetching over an answer, over an
 * error, or over nothing, and all three combinations are reachable.
 */
type Settled<T> =
  | { readonly status: 'never' }
  | { readonly status: 'answer'; readonly answer: QueryAnswer<T> }
  | { readonly status: 'error'; readonly error: Error; readonly previous: QueryAnswer<T> | null };

/** The in-flight half of the state. `debouncing` counts as in flight. */
type Phase = 'idle' | 'debouncing' | 'fetching';

/**
 * Both halves in ONE observable, deliberately.
 *
 * Two separate observables would mean two atoms, and the observation lifecycle
 * has to key on exactly one of them: a consumer reading only the other would
 * either fail to arm the query or let it be torn down while still on screen.
 * One record also makes every publish atomic — no observer can catch a new
 * answer under the old phase.
 */
interface QueryState<T> {
  readonly settled: Settled<T>;
  readonly phase: Phase;
}

/** The answer currently held, wherever in `settled` it happens to sit. */
const heldAnswer = <T>(settled: Settled<T>): QueryAnswer<T> | null => {
  switch (settled.status) {
    case 'answer':
      return settled.answer;
    case 'error':
      return settled.previous;
    case 'never':
      return null;
  }
};

/**
 * Projects the internal state onto what a consumer renders.
 *
 * Pure, and shared with the resolution path so a superseded fetch can report its
 * own outcome to whoever awaited it without publishing it to everyone else.
 */
const project = <T>({ settled, phase }: QueryState<T>): QueryValue<T> => {
  if (phase !== 'idle') {
    const held = heldAnswer(settled);
    return held === null ? { kind: 'loading' } : { kind: 'stale', previous: held };
  }
  switch (settled.status) {
    case 'never':
      return { kind: 'uninitialized' };
    case 'answer':
      return { kind: 'resolved', answer: settled.answer };
    case 'error':
      return { kind: 'error', error: settled.error, previous: settled.previous };
  }
};

/**
 * A remote value derived from observable parameters.
 *
 * Runs `fetch` whenever `params` changes, publishes the outcome as one
 * observable {@link QueryValue}, and keeps the previous answer visible while the
 * next one is in flight. Replaces the reaction-plus-sequence-number-plus-flags
 * arrangement that each consumer would otherwise hand-roll.
 *
 * **Nothing happens until the value is observed.** Reading `value` outside a
 * reaction returns a snapshot and does not arm anything, so a query read only
 * from a non-reactive context stays `uninitialized` forever. That is the point —
 * it is what lets a query belong to a dialog and cost nothing while the dialog is
 * closed — but it means an imperative caller must go through {@link refresh},
 * which asks unconditionally and resolves with the outcome.
 *
 * Superseded responses are dropped by an epoch counter rather than by comparing
 * parameters: `refresh` re-asks at *unchanged* parameters, so parameter equality
 * cannot tell an overtaken answer from the current one.
 */
export class MobxQuery<P, T> {
  private readonly _debounceMs: number;
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _disposeParams: (() => void) | null = null;
  private readonly _equals: IEqualsComparer<P>;
  private _epoch = 0;
  private readonly _fetch: (params: P) => Promise<T>;
  private readonly _graceMs: number;
  private _graceTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * The parameters of the most recent request, boxed so "never asked" stays
   * distinguishable from "asked with `undefined`".
   */
  private _lastParams: { readonly params: P } | null = null;
  private readonly _params: () => P;
  private _settledWaiters: Array<() => void> = [];
  private _state: QueryState<T> = { settled: { status: 'never' }, phase: 'idle' };

  constructor(options: MobxQueryOptions<P, T>) {
    this._params = options.params;
    this._fetch = options.fetch;
    this._debounceMs = options.debounceMs;
    this._equals = options.equals ?? comparer.structural;
    this._graceMs = options.graceMs ?? DEFAULT_GRACE_MS;

    makeObservable<MobxQuery<P, T>, '_state'>(this, {
      _state: observable.ref,
      value: computed,
    });

    // Arming is driven by observation rather than by an explicit `start()`: a
    // remount re-observes and so re-arms by itself, which is the failure mode a
    // hand-rolled start/dispose pair exists to work around.
    onBecomeObserved(this, '_state', this._observed);
    onBecomeUnobserved(this, '_state', this._unobserved);
  }

  // ── Accessors ─────────────────────────────────────────────────────────────

  /**
   * The state as it stands, read without subscribing.
   *
   * For the query's own internals: publishing must never accidentally count as
   * observing, or a query would keep itself armed forever.
   */
  private get _current(): QueryState<T> {
    return untracked(() => this._state);
  }

  /**
   * What to render. Reading this inside a reaction is what arms the query.
   *
   * `computed` so repeated reads within one derivation share an identity —
   * consumers memoize on it.
   */
  get value(): QueryValue<T> {
    return project(this._state);
  }

  // ── Methods ───────────────────────────────────────────────────────────────

  /**
   * Tears the query down now instead of after the grace period.
   *
   * Not needed on any production path — losing the last observer already tears
   * down by itself. It exists for **test hygiene**: a query leaves a grace timer
   * behind, and under fake timers a pending timeout that outlives its test fires
   * during a later one. Dispose owned queries in `afterEach`, the same reason
   * models with live reactions are disposed there.
   *
   * Also serves a deliberate teardown (a dialog closing for good) that wants an
   * in-flight request abandoned immediately rather than a second later.
   */
  dispose = (): void => {
    this._teardown();
  };

  /**
   * Asks again for the current parameters, skipping the debounce, and resolves
   * with the outcome.
   *
   * Deliberately unconditional: the answer may depend on something the parameters
   * cannot express (the clock, a value another client changed), so "same
   * parameters" is not a reason to skip the request.
   *
   * Never rejects. A failure is already reported through `value`, and a second
   * error channel for one failure is a channel someone handles twice or not at
   * all. The resolved {@link QueryValue} is this call's own outcome, which is
   * what an imperative caller wants even when a newer request has superseded it
   * for everyone else.
   */
  refresh = (): Promise<QueryValue<T>> => {
    this._cancelDebounce();
    return this._run(untracked(this._params));
  };

  /**
   * Resolves once nothing is in flight and no debounce is pending.
   *
   * Does NOT wait for a parameter change to propagate — a caller that has just
   * written one must let the reaction fire first. Exists for tests: recomputation
   * is reactive, so there is no call to await.
   */
  whenSettled = (): Promise<void> => {
    if (this._current.phase === 'idle') {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => this._settledWaiters.push(resolve));
  };

  // ── Private ───────────────────────────────────────────────────────────────

  private _cancelDebounce = (): void => {
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  };

  private _cancelGrace = (): void => {
    if (this._graceTimer !== null) {
      clearTimeout(this._graceTimer);
      this._graceTimer = null;
    }
  };

  private _flushWaiters = (): void => {
    this._settledWaiters.splice(0).forEach((resolve) => resolve());
  };

  /** The last observer left, or the owner disposed us. */
  private _unobserved = (): void => {
    this._cancelGrace();
    if (this._graceMs <= 0) {
      this._teardown();
      return;
    }
    this._graceTimer = setTimeout(() => {
      this._graceTimer = null;
      this._teardown();
    }, this._graceMs);
  };

  /** Somebody started reading `value` inside a reaction. */
  private _observed = (): void => {
    this._cancelGrace();
    // Still armed: this is a re-observation inside the grace window, so the
    // reaction never died and its parameter subscription is intact.
    if (this._disposeParams !== null) {
      return;
    }

    this._disposeParams = reaction(() => this._params(), this._schedule, { equals: this._equals });

    // A held answer for unchanged parameters is still the answer; anything else
    // (never asked, or an error) is asked again. Parameters can have moved while
    // nobody was watching, since the subscription above did not exist then.
    const params = untracked(this._params);
    const settled = this._current.settled;
    if (settled.status === 'answer' && this._lastParams !== null && this._equals(this._lastParams.params, params)) {
      return;
    }

    // Deferred one microtask: this hook runs while the derivation that just read
    // `_state` is still binding its dependencies, and the first request publishes
    // a `fetching` phase — writing a dependency of a derivation mid-tracking. The
    // reaction above is safe to create here (it only reads), so a parameter change
    // arriving before the microtask still schedules normally — and the guards below
    // then stand down rather than duplicating that request.
    const armEpoch = this._epoch;
    void Promise.resolve().then(() => {
      if (this._disposeParams === null || this._epoch !== armEpoch || this._current.phase === 'debouncing') {
        return;
      }
      void this._run(untracked(this._params));
    });
  };

  /**
   * Runs `fetch` and publishes the outcome if it is still the current one.
   *
   * `asOf` is captured before the request goes out, not when it lands: the answer
   * describes the world as it stood when asked, and a round trip can take
   * seconds.
   */
  private _run = (params: P): Promise<QueryValue<T>> => {
    const epoch = ++this._epoch;
    const asOf = new Date();
    this._lastParams = { params };
    this._setState({ settled: this._current.settled, phase: 'fetching' });

    return this._fetch(params).then(
      (value) => this._settle(epoch, { status: 'answer', answer: { value, asOf } }),
      // `toError` unwraps an axios failure to what the backend actually complained
      // about, and passes a plain Error through untouched — so a `fetch` that
      // already mapped its own errors is unaffected.
      (thrown: unknown) => this._settle(epoch, { status: 'error', error: toError(thrown), previous: heldAnswer(this._current.settled) })
    );
  };

  /**
   * Marks the value out of date now and defers the request.
   *
   * Splitting the two is the whole point of the debounce: a consumer learns
   * immediately that what it is showing is superseded, without a request per
   * keystroke.
   */
  private _schedule = (params: P): void => {
    this._cancelDebounce();
    if (this._debounceMs <= 0) {
      void this._run(params);
      return;
    }
    this._setState({ settled: this._current.settled, phase: 'debouncing' });
    this._debounceTimer = setTimeout(() => {
      this._debounceTimer = null;
      void this._run(params);
    }, this._debounceMs);
  };

  private _setState = (state: QueryState<T>): void => {
    this._state = state;
  };

  /** Commits an outcome, unless a later request has already taken over. */
  private _settle = (epoch: number, settled: Settled<T>): QueryValue<T> => {
    const outcome = project({ settled, phase: 'idle' });
    if (epoch !== this._epoch) {
      return outcome; // superseded: report to whoever awaited this call, publish nothing
    }
    this._setState({ settled, phase: 'idle' });
    this._flushWaiters();
    return outcome;
  };

  /**
   * Stops asking. The held answer survives, so a later re-observation can show it
   * at once; the epoch bump makes sure an in-flight response cannot land on top of
   * whatever that re-observation fetches.
   */
  private _teardown = (): void => {
    this._cancelGrace();
    this._cancelDebounce();
    this._disposeParams?.();
    this._disposeParams = null;
    this._epoch += 1;

    const current = this._current;
    if (current.phase !== 'idle') {
      this._setState({ settled: current.settled, phase: 'idle' });
    }
    // Nothing is in flight any more, so anyone waiting for quiescence has it.
    this._flushWaiters();
  };
}
